import type { IntlShape } from 'react-intl'
import type { UiText } from '@ory/client'

// "Your changes have been saved!" — Kratos stable message id for any successful
// settings flow update. Lives here next to the catalog so consumers don't have
// to redefine the magic number.
// https://www.ory.sh/docs/kratos/concepts/ui-user-interface#message-codes
export const KRATOS_MSG_SETTINGS_UPDATED = 1050001

// Kratos puts the user back on /login with one of these ids after they click
// an OIDC provider whose verified email matches an existing password identity.
// LoginPage surfaces them as a prominent banner + hides OIDC buttons so the
// user types their password instead of re-clicking the OIDC button.
//   1010016 InfoSelfServiceLoginLink
//   1010017 InfoSelfServiceLoginAndLink
//   1010018 InfoSelfServiceLoginWithAndLink
//   4000028 ErrorValidationDuplicateCredentialsOnOIDCLink
//   4000029 ErrorValidationDuplicateCredentialsWithHints
export const KRATOS_LINKING_MESSAGE_IDS: ReadonlySet<number> = new Set([
  1010016, 1010017, 1010018, 4000028, 4000029,
])

export const isLinkingMessage = (m: UiText): boolean => KRATOS_LINKING_MESSAGE_IDS.has(m.id)

// Set so we warn at most once per id per session — Kratos repeats the same
// message id across renders and we don't want to spam the console. Exposed
// for tests / observability tooling that wants to surface drift after a
// `@ory/client` bump.
const observedMisses = new Set<number>()

export const getKratosI18nMisses = (): ReadonlyArray<number> => Array.from(observedMisses)

// Test-only seam — production code never calls this. Kept in this module so
// it shares the closure rather than reaching for a singleton import path.
export const __resetKratosI18nMisses = (): void => {
  observedMisses.clear()
}

const noteMiss = (id: number): void => {
  if (observedMisses.has(id)) {
    return
  }
  observedMisses.add(id)
  if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
    // eslint-disable-next-line no-console
    console.warn(`[kratos-i18n] no catalog entry for id=${id}; falling back to upstream .text`)
  }
}

// Kratos emits stable numeric IDs for every UiText (4000xxx errors, 1010xxx
// info, 1050xxx success). Look the ID up in the catalog and format with
// `m.context` for placeholder substitution; fall back to the server-rendered
// English `m.text` when an ID isn't translated yet. Never throw — bad context
// shapes on unfamiliar IDs would otherwise break a whole login screen.
export const translateKratosText = (m: UiText, intl: IntlShape): string => {
  const key = `kratos.${m.id}`
  if (!(key in intl.messages)) {
    noteMiss(m.id)
    return m.text
  }
  try {
    const ctx =
      m.context && typeof m.context === 'object'
        ? (m.context as Record<string, string | number | boolean | Date>)
        : undefined
    return intl.formatMessage({ id: key }, ctx)
  } catch {
    return m.text
  }
}
